import { calculatePartialDateDiffenceDuration, isPartialDate, nowPartialDate, sortPartialDate } from 'neuro-utils';
import type { ClinicalArchive } from '.';
import type { LoadableDocConfig } from '../generalSettings';
import { parkinsonInfusionMedicationUnfilledDose } from 'Routes/Medication/utils/parkinsonUnfilledFields';
import { parkinsonDiagnoses } from 'Routes/Diagnosis/utils/definitions';

export const parkinsonDocumentTypes: LoadableDocConfig[] = [
  {
    name: 'diagnosis',
    subTypes: ['diagnosis', 'parkinsonSymptoms', 'pd', 'msa', 'psp', 'dlb', 'cbd', 'ftd', 'ppa', 'alzheimer'],
    secondaryDocs: ['cdr', 'examination'],
  },
  {
    name: 'diagnosticSurvey',
    subTypes: ['npi', 'fbimod', 'bnsq', 'adl', 'iadl'],
    secondaryDocs: ['diagnosis', 'mmse'],
  },
  { name: 'medication', subTypes: ['medication', 'adverseEffect'] },
  { name: 'dbs' },
  { name: 'thalamotomyHifu', subTypes: ['thalamotomy', 'hifu'] },
  {
    name: 'updrs',
    subTypes: ['updrs_iii', 'updrs_v', 'ftmtrs', 'levodopatest', 'dbsEffect'],
    secondaryDocs: ['dbs'],
  },
  { name: 'cognition', subTypes: ['moca', 'mmse', 'tmt', 'cdr', 'sdmt', 'cerad'], secondaryDocs: ['background'] },
  { name: 'imaging', subTypes: ['mri', 'tt', 'scintigraphy'] },
  { name: 'ariaSymptoms' },
  { name: 'geneTest' },
  { name: 'examination' },
  { name: 'background', subTypes: ['background', 'measurement'] },
  { name: 'lifestyle' },
  { name: 'myService', title: 'titleShort' },
] as { name: string; subTypes?: string[]; secondaryDocs?: string[] }[];

export const parkinsonGraphDocTypes = [
  'diagnosis',
  'geneTest',
  'thalamotomy',
  'hifu',
  'updrs_v',
  'updrs_iii',
  'ftmtrs',
  'medication',
  'adverseEffect',
  'moca',
  'mmse',
  'tmt',
  'cdr',
  'dbsEffect',
  'dbs',
  'levodopatest',
  'sdmt',
  'mri',
  'tt',
  'scintigraphy',
  'fbimod',
  'examination',
  'adl',
  'iadl',
  'cerad',
  'ariaSymptoms',
];

// Filter these routes/types from showing in page navigations based on these rules
export const parkinsonFilteredTypes = (docs?: Array<IControlProps & { [key: string]: any }>): string[] => {
  let parkDiagWithDbs: IDiagnosis | undefined = undefined;
  let dbsDocs: Array<IDBS> = [];
  let parkDiagWithDbsIndex = -1;
  let alzheimerDiag = false;
  let thalamotomyDocs: Array<IThalamotomy> = [];
  let hifuDocs: Array<IHifuTreatment> = [];

  const parkinsonDiags = [
    'G20',
    'G23.1',
    'G90.3',
    'G23.2',
    'G23.3',
    'G31.0',
    'G25.9',
    'F02.8*G31.8',
    'F00.2*G30.8',
    'F02.0*G31.0',
    'G25',
    'G25.0',
    'G25.1#',
    'G25.2',
    'G25.3#',
    'G25.4#',
    'G25.5',
    'G25.6#',
    'G25.8',
  ];
  const diagnosesToShowDBS = ['G20', 'G25.0'];
  const alzheimerDiags = ['G30', 'G30.0', 'G30.1', 'G30.8', 'G30.9', 'R41.8'];

  if (docs && docs.length > 0) {
    const diagnosisDocs: IDiagnosis[] | undefined = docs.filter(d => d._type === 'diagnosis');
    const parkinsonDiagDocs = diagnosisDocs.filter(
      doc => doc.diagnosis && typeof doc.diagnosis === 'string' && parkinsonDiags.includes(doc.diagnosis),
    );
    parkDiagWithDbs = parkinsonDiagDocs?.find(
      d => d.diagnosis && typeof d.diagnosis === 'string' && diagnosesToShowDBS.includes(d.diagnosis),
    );
    parkDiagWithDbsIndex = parkinsonDiagDocs
      .toSorted((d1, d2) => {
        if ('date' in d1 && 'date' in d2 && isPartialDate(d1.date) && isPartialDate(d2.date)) {
          return sortPartialDate(d2.date, d1.date);
        }
        return 0;
      })
      .findIndex(d => d.diagnosis && typeof d.diagnosis === 'string' && diagnosesToShowDBS.includes(d.diagnosis));

    dbsDocs = docs.filter(d => d._type === 'dbs');
    thalamotomyDocs = docs.filter(d => d._type === 'thalamotomy');
    hifuDocs = docs.filter(d => d._type === 'hifu');

    alzheimerDiag = !!diagnosisDocs?.find(
      diag => diag.diagnosis && typeof diag.diagnosis === 'string' && alzheimerDiags.includes(diag.diagnosis),
    );
  }

  const filteredSections = [];
  if (!alzheimerDiag) {
    filteredSections.push('ariaSymptoms');
  }
  // Include DBS and thalamotomyHifu if ( G20 or G25.0 diagnosis is latest movementDisorderDiagnosis AND no alzheimer diagnosis found)
  if (!(parkDiagWithDbs && parkDiagWithDbsIndex === 0 && !alzheimerDiag)) {
    // If dbs docs found, do not filter the tile
    if (dbsDocs.length === 0) {
      filteredSections.push('dbs');
    }
    // If thalamotomy or hifu docs found, do not filter the tile
    if (thalamotomyDocs.length === 0 && hifuDocs.length === 0) {
      filteredSections.push('thalamotomyHifu');
    }
  }
  return filteredSections;
};

export const parkinsonAlerts: IAlert[] = [
  {
    // Notification to the user if the estimated time to replace the DBS generator is less than one year.
    content: 'alerts.parkinson.dbsOneYear',
    id: 'parkinsonDbsOneYear',
    severity: 'warning',
    popup: true,
    rule: (docs?: INeuroUIDocument[]): boolean => {
      // Find first dbs document (docs should be sorted descending by commitDate)
      const dbsDoc = ((docs || []).find(d => d._type === 'dbs') as IDBS) || {};
      const generatorChangeDate = dbsDoc.generators?.[0]?.generatorNextChangeDate;
      if (!generatorChangeDate) return false;
      const duration = calculatePartialDateDiffenceDuration('days', generatorChangeDate);
      return dbsDoc.status !== 'ended' && Math.abs(duration.days) < 365;
    },
  },
  {
    // Notification if infusion medication documents from integration has unfilled regimen doses or dose times
    content: 'alerts.parkinson.infusionMedicationUnfilledDose',
    id: 'parkinsonInfusionMedicationUnfilledDose',
    severity: 'info',
    popup: true,
    rule: parkinsonInfusionMedicationUnfilledDose,
  },
  {
    // Notification to the user if a diagnosis document is recorded before a parkinsonSymptoms document
    content: 'alerts.parkinson.symptomOnsetDateAfterDiagnosisDate',
    id: 'parkinsonSymptomOnsetDateAfterDiagnosisDate',
    severity: 'warning',
    popup: true,
    rule: (docs?: Array<IControlProps & Record<string, unknown>>): boolean => {
      // Find first Parkinson diagnosis document (docs should be sorted descending by commitDate)
      const parkinsonDiagnosisDoc: IDiagnosis | Record<string, never> =
        (docs || []).find(
          d =>
            d._type === 'diagnosis' &&
            'diagnosis' in d &&
            (parkinsonDiagnoses as string[]).includes((d as IDiagnosis).diagnosis || ''),
        ) || {};
      const parkinsonDiagnosisDate = parkinsonDiagnosisDoc.date;

      // Find first Parkinson diagnosis document (docs should be sorted descending by commitDate)
      const parkinsonSymptomsDoc: Partial<IParkinsonSymptoms> | Record<string, never> =
        (docs || []).find(d => d._type === 'parkinsonSymptoms') || {};
      const parkinsonSymptomOnsetDate = parkinsonSymptomsDoc.symptomOnsetDate;

      if (!parkinsonDiagnosisDate || !parkinsonSymptomOnsetDate) return false;
      return sortPartialDate(parkinsonSymptomOnsetDate, parkinsonDiagnosisDate) > 0;
    },
  },
  {
    // Notification if MRI has been filled with ARIA findings to remind filling ARIA symptoms document
    content: 'alerts.parkinson.alzheimerAriaSymptomsReminder',
    id: 'alzheimerMRIAriaFindingsReminder',
    severity: 'info',
    popup: true,
    rule: (docs?: INeuroUIDocument[]): boolean => {
      const ariaSymptomDocs = docs?.filter(d => d._type === 'ariaSymptoms') as IAriaSymptoms[] | undefined;

      // 1. Get all medications related to ARIA (e.g. Lecanemab)
      const medicationDocsRelatedToAria = docs?.filter(
        d => d._type === 'medication' && medicationATCsForAria.includes((d as IMedication).atc ?? ''),
      ) as IMedication[] | undefined;

      // If no ARIA-related medication exists, do not show the reminder
      if (!medicationDocsRelatedToAria || medicationDocsRelatedToAria.length === 0) {
        return false;
      }

      // 2. Find the earliest start date of the relevant medication
      const earliestMedicationDate = medicationDocsRelatedToAria
        .map(m => m.startDate)
        .toSorted((a, b) => sortPartialDate(a, b))[0];

      // 3. Filter for MRI documents that have ARIA findings AND occurred AFTER the medication started
      const mriDocsWithAriaAfterMedication = docs?.filter(d => {
        if (d._type !== 'mri' || d._editing) return false;
        const mri = d as IMRI;

        const hasAriaFindings = (mri.ariaE?.finding && mri.ariaE?.finding !== 'no') || mri.ariaH?.finding === 'yes';

        // Check if the MRI date is the same as or later than the medication start date
        const isAfterOrOnMedicationStart = sortPartialDate(mri.date, earliestMedicationDate) >= 0;

        return hasAriaFindings && isAfterOrOnMedicationStart;
      }) as IMRI[] | undefined;

      // If no relevant MRI findings exist post-medication start, do not show the reminder
      if (!mriDocsWithAriaAfterMedication || mriDocsWithAriaAfterMedication.length === 0) {
        return false;
      }

      // 4. Get the latest MRI with findings and the latest ARIA symptoms document
      const latestMriWithAria = mriDocsWithAriaAfterMedication.toSorted((m1, m2) =>
        sortPartialDate(m2.date, m1.date),
      )[0];

      const latestAriaSymptoms = ariaSymptomDocs?.toSorted((a1, a2) => sortPartialDate(a2.date, a1.date))[0];

      // 5. Trigger reminder only if the latest post-medication MRI is newer than the latest symptom documentation
      return sortPartialDate(latestMriWithAria.date, latestAriaSymptoms?.date) === 1;
    },
    fillNewButton: { route: 'ariaSymptoms', documentType: 'ariaSymptoms', initData: { date: nowPartialDate() } },
  },
];

export const medicationATCsForAria = ['N06DX04', 'N06DX05']; // lekanemabi, donanemabi

export const parkinsonClinicalArchiveTypes: readonly ClinicalArchive[] = ['NEU', 'HOI'] as const;
